/**
 * Engagement Tracking Utility (Stage 1)
 * 
 * Simple utility to log user engagement events for analytics.
 * No UI, no dashboards - just data collection.
 */

import axios from '../lib/apiTransport';

const API_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL || 'http://localhost:8001';

/**
 * Log an engagement event
 * @param {string} eventType - Type of event (app_load, join_game, switch_team, ready_toggle)
 * @param {object} options - Optional parameters
 * @param {string} options.gameId - Game ID if applicable
 * @param {object} options.metadata - Additional event metadata
 * @returns {Promise<object>} Response with event_id and timestamp
 */
export async function logEngagementEvent(eventType, options = {}) {
  try {
    const token = localStorage.getItem('access_token');
    
    // Don't log if user is not authenticated
    if (!token) {
      return null;
    }

    const payload = {
      event_type: eventType,
      game_id: options.gameId || null,
      metadata: options.metadata || {}
    };

    const response = await axios.post(
      `${API_URL}/api/engagement/log-event`,
      payload,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    // Silently fail - don't disrupt user experience
    return null;
  }
}

/**
 * Log app load event
 */
export function logAppLoad() {
  logEngagementEvent('app_load', {
    metadata: {
      url: window.location.pathname,
      referrer: document.referrer || 'direct',
      screen_width: window.screen.width,
      screen_height: window.screen.height
    }
  });
}

/**
 * Log join game event
 * @param {string} gameId - Game ID
 * @param {string} team - Team joined (A or B)
 */
export function logJoinGame(gameId, team) {
  logEngagementEvent('join_game', {
    gameId,
    metadata: { team }
  });
}

/**
 * Log switch team event
 * @param {string} gameId - Game ID
 * @param {string} fromTeam - Original team
 * @param {string} toTeam - New team
 */
export function logSwitchTeam(gameId, fromTeam, toTeam) {
  logEngagementEvent('switch_team', {
    gameId,
    metadata: { from_team: fromTeam, to_team: toTeam }
  });
}

/**
 * Log ready toggle event
 * @param {string} gameId - Game ID
 * @param {boolean} isReady - Ready state
 */
export function logReadyToggle(gameId, isReady) {
  logEngagementEvent('ready_toggle', {
    gameId,
    metadata: { is_ready: isReady }
  });
}

/**
 * Log profile view event
 * @param {object} metadata - Profile state metadata
 * @param {boolean} metadata.has_bio - Whether user has a bio
 * @param {boolean} metadata.has_avatar - Whether user has an avatar
 * @param {boolean} metadata.has_address - Whether user has a home address
 */
export function logProfileView(metadata = {}) {
  logEngagementEvent('profile_view', { metadata });
}

/**
 * Log address autocomplete used event
 * @param {object} addressData - Address data from autocomplete
 */
export function logAddressAutocompleteUsed(addressData = {}) {
  const metadata = {
    city: addressData.city || '',
    state: addressData.state || '',
    zip_code: addressData.zip_code || '',
    country: addressData.country || 'US',
    has_lat_lng: !!(addressData.lat && addressData.lng)
  };
  logEngagementEvent('address_autocomplete_used', { metadata });
}

/**
 * Get engagement events for current user
 * @param {object} options - Query options
 * @param {number} options.limit - Max events to fetch
 * @param {string} options.eventType - Filter by event type
 * @returns {Promise<object>} Events data
 */
export async function getUserEngagementEvents(options = {}) {
  try {
    const token = localStorage.getItem('access_token');
    if (!token) return null;

    // Get current user ID from token (JWT decode)
    const payload = JSON.parse(atob(token.split('.')[1]));
    const userId = payload.sub;

    const params = new URLSearchParams();
    if (options.limit) params.append('limit', options.limit);
    if (options.eventType) params.append('event_type', options.eventType);

    const response = await axios.get(
      `${API_URL}/api/engagement/by-user/${userId}?${params}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    return response.data;
  } catch (error) {
    return null;
  }
}

export default {
  logEngagementEvent,
  logAppLoad,
  logJoinGame,
  logSwitchTeam,
  logReadyToggle,
  logProfileView,
  logAddressAutocompleteUsed,
  getUserEngagementEvents
};
